import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { questionService } from '@/features/admin/services/questionService';
import { categoryService, CategoryDto } from '@/features/admin/services/categoryService';
import { toast } from 'sonner';
import { 
  FileUp, 
  Download, 
  ArrowLeft, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  Table,
  FileSpreadsheet,
  HelpCircle,
  Trash2,
  UserPlus,
  Megaphone,
  Power
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { useEffect } from 'react';

interface ParsedQuestion {
  rowNumber: number;
  categoryName: string;
  questionTextAr: string;
  questionText?: string;
  answerAr: string;
  answer?: string;
  difficulty: string;
  timeLimit?: number;
  explanationAr?: string;
  explanation?: string;
  // Validation
  isValid: boolean;
  errors: string[];
  categoryId?: string;
}

export const ImportQuestionsPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [categories, setCategories] = useState<CategoryDto[]>([]);
  const [parsedQuestions, setParsedQuestions] = useState<ParsedQuestion[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [enableForNewUsers, setEnableForNewUsers] = useState(false);
  const [isPromotionQuestion, setIsPromotionQuestion] = useState(false);
  const [isActiveDirectly, setIsActiveDirectly] = useState(false);
  const [lastImportedIds, setLastImportedIds] = useState<string[]>([]);
  const [deletingImported, setDeletingImported] = useState(false);
  const [rawSheetData, setRawSheetData] = useState<any[]>([]);

  useEffect(() => {
    fetchCategories();
  }, []);

  // إعادة التحقق من الأسئلة عند تحميل/تحديث الفئات (مثلاً عند قاعدة بيانات كانت فارغة ثم امتلأت)
  useEffect(() => {
    if (categories.length > 0 && rawSheetData.length > 0) {
      setParsedQuestions(parseAndValidateQuestions(rawSheetData));
    }
  }, [categories]);

  const fetchCategories = async () => {
    try {
      const data = await categoryService.getAllCategories({ pageSize: 1000 });
      const items = data?.items ?? [];
      setCategories(Array.isArray(items) ? items.filter((c: CategoryDto) => c.isActive) : []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      setCategories([]);
    }
  };

  // تحميل قالب Excel
  const downloadTemplate = () => {
    // إنشاء بيانات المثال
    const exampleData = [
      {
        'اسم الفئة': 'صحح الخطأ',
        'نص السؤال (عربي)': 'ما هي عاصمة المملكة العربية السعودية؟',
        'نص السؤال (إنجليزي)': 'What is the capital of Saudi Arabia?',
        'الإجابة (عربي)': 'الرياض',
        'الإجابة (إنجليزي)': 'Riyadh',
        'الصعوبة': 'سهل',
        'الوقت (ثانية)': 30,
        'شرح الإجابة (عربي)': 'الرياض هي عاصمة المملكة العربية السعودية وأكبر مدنها',
        'شرح الإجابة (إنجليزي)': 'Riyadh is the capital and largest city of Saudi Arabia',
      },
      {
        'اسم الفئة': 'صحح الخطأ',
        'نص السؤال (عربي)': 'كم عدد ألوان قوس قزح؟',
        'نص السؤال (إنجليزي)': '',
        'الإجابة (عربي)': 'سبعة ألوان',
        'الإجابة (إنجليزي)': '',
        'الصعوبة': 'سهل',
        'الوقت (ثانية)': 30,
        'شرح الإجابة (عربي)': '',
        'شرح الإجابة (إنجليزي)': '',
      },
      {
        'اسم الفئة': 'صحح الخطأ',
        'نص السؤال (عربي)': 'من هو مؤسس شركة مايكروسوفت؟',
        'نص السؤال (إنجليزي)': 'Who founded Microsoft?',
        'الإجابة (عربي)': 'بيل غيتس',
        'الإجابة (إنجليزي)': 'Bill Gates',
        'الصعوبة': 'متوسط',
        'الوقت (ثانية)': 45,
        'شرح الإجابة (عربي)': 'أسس بيل غيتس مع بول آلن شركة مايكروسوفت عام 1975',
        'شرح الإجابة (إنجليزي)': 'Bill Gates co-founded Microsoft with Paul Allen in 1975',
      },
      {
        'اسم الفئة': 'صحح الخطأ',
        'نص السؤال (عربي)': 'ما هو أكبر كوكب في المجموعة الشمسية؟',
        'نص السؤال (إنجليزي)': '',
        'الإجابة (عربي)': 'المشتري',
        'الإجابة (إنجليزي)': 'Jupiter',
        'الصعوبة': 'صعب',
        'الوقت (ثانية)': 60,
        'شرح الإجابة (عربي)': '',
        'شرح الإجابة (إنجليزي)': '',
      },
    ];

    // إنشاء workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exampleData);

    // تعيين عرض الأعمدة
    ws['!cols'] = [
      { wch: 15 },  // اسم الفئة
      { wch: 50 },  // نص السؤال (عربي)
      { wch: 50 },  // نص السؤال (إنجليزي)
      { wch: 25 },  // الإجابة (عربي)
      { wch: 25 },  // الإجابة (إنجليزي)
      { wch: 10 },  // الصعوبة
      { wch: 12 },  // الوقت
      { wch: 40 },  // شرح الإجابة (عربي)
      { wch: 40 },  // شرح الإجابة (إنجليزي)
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'الأسئلة');

    // إضافة sheet التعليمات
    const instructionsData = [
      ['📋 تعليمات استيراد الأسئلة'],
      [''],
      ['الحقول المطلوبة:'],
      ['- اسم الفئة: يجب أن يكون اسم فئة موجودة في النظام'],
      ['- نص السؤال (عربي): نص السؤال باللغة العربية'],
      ['- الإجابة (عربي): الإجابة الصحيحة باللغة العربية'],
      ['- الصعوبة: سهل أو متوسط أو صعب'],
      [''],
      ['الحقول الاختيارية:'],
      ['- نص السؤال (إنجليزي): نص السؤال باللغة الإنجليزية'],
      ['- الإجابة (إنجليزي): الإجابة باللغة الإنجليزية'],
      ['- الوقت (ثانية): الوقت المحدد للإجابة (5-120 ثانية، الافتراضي 30)'],
      ['- شرح الإجابة (عربي): شرح للإجابة الصحيحة'],
      ['- شرح الإجابة (إنجليزي): شرح بالإنجليزية'],
      [''],
      ['ملاحظات هامة:'],
      ['- تأكد من وجود الفئة في النظام قبل الاستيراد'],
      ['- الصعوبة: سهل (200 نقطة)، متوسط (400 نقطة)، صعب (600 نقطة)'],
      ['- يمكنك إضافة الصور لاحقاً من خلال تعديل كل سؤال'],
    ];
    const wsInstructions = XLSX.utils.aoa_to_sheet(instructionsData);
    wsInstructions['!cols'] = [{ wch: 80 }];
    XLSX.utils.book_append_sheet(wb, wsInstructions, 'التعليمات');

    // تحميل الملف
    XLSX.writeFile(wb, 'قالب_استيراد_الاسئلة.xlsx');
    toast.success('تم تحميل القالب بنجاح');
  };

  // معالجة الملف المرفوع
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // التحقق من نوع الملف
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    if (!validTypes.includes(file.type) && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('يرجى رفع ملف Excel فقط (.xlsx أو .xls)');
      return;
    }

    setFileName(file.name);
    setLoading(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // قراءة الورقة الأولى
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        setRawSheetData(Array.isArray(jsonData) ? jsonData : []);

        // تحويل البيانات وvalidation
        const parsed = parseAndValidateQuestions(jsonData);
        setParsedQuestions(parsed);
        
        const validCount = parsed.filter(q => q.isValid).length;
        const invalidCount = parsed.filter(q => !q.isValid).length;
        
        if (invalidCount > 0) {
          toast.warning(`تم قراءة ${parsed.length} سؤال: ${validCount} صالح، ${invalidCount} يحتاج مراجعة`);
        } else {
          toast.success(`تم قراءة ${validCount} سؤال بنجاح`);
        }
      } catch (error) {
        console.error('Error parsing Excel:', error);
        toast.error('فشل قراءة الملف. تأكد من صحة تنسيق Excel');
      } finally {
        setLoading(false);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // تحليل والتحقق من البيانات
  const parseAndValidateQuestions = (data: any[]): ParsedQuestion[] => {
    return data.map((row, index) => {
      const errors: string[] = [];
      
      // استخراج البيانات
      const categoryName = (row['اسم الفئة'] || '').toString().trim();
      const questionTextAr = (row['نص السؤال (عربي)'] || '').toString().trim();
      const questionText = (row['نص السؤال (إنجليزي)'] || '').toString().trim();
      const answerAr = (row['الإجابة (عربي)'] || '').toString().trim();
      const answer = (row['الإجابة (إنجليزي)'] || '').toString().trim();
      const difficultyRaw = (row['الصعوبة'] || '').toString().trim();
      const timeLimit = parseInt(row['الوقت (ثانية)']) || 30;
      const explanationAr = (row['شرح الإجابة (عربي)'] || '').toString().trim();
      const explanation = (row['شرح الإجابة (إنجليزي)'] || '').toString().trim();

      // التحقق من الحقول المطلوبة
      if (!categoryName) errors.push('اسم الفئة مطلوب');
      if (!questionTextAr) errors.push('نص السؤال (عربي) مطلوب');
      if (!answerAr) errors.push('الإجابة (عربي) مطلوبة');
      if (!difficultyRaw) errors.push('الصعوبة مطلوبة');

      // تحويل الصعوبة
      let difficulty = 'Medium';
      if (difficultyRaw === 'سهل' || difficultyRaw.toLowerCase() === 'easy') {
        difficulty = 'Easy';
      } else if (difficultyRaw === 'متوسط' || difficultyRaw.toLowerCase() === 'medium') {
        difficulty = 'Medium';
      } else if (difficultyRaw === 'صعب' || difficultyRaw.toLowerCase() === 'hard') {
        difficulty = 'Hard';
      } else if (difficultyRaw) {
        errors.push('الصعوبة يجب أن تكون: سهل، متوسط، أو صعب');
      }

      // التحقق من وجود الفئة
      const category = categories.find(
        c => c.name.toLowerCase() === categoryName.toLowerCase() || 
             c.nameAr?.toLowerCase() === categoryName.toLowerCase()
      );
      if (categoryName && !category) {
        errors.push(`الفئة "${categoryName}" غير موجودة في النظام`);
      }

      // التحقق من الوقت
      if (timeLimit < 5 || timeLimit > 120) {
        errors.push('الوقت يجب أن يكون بين 5 و 120 ثانية');
      }

      return {
        rowNumber: index + 2, // +2 لأن الصف 1 هو العنوان
        categoryName,
        categoryId: category?.id,
        questionTextAr,
        questionText: questionText || undefined,
        answerAr,
        answer: answer || undefined,
        difficulty,
        timeLimit,
        explanationAr: explanationAr || undefined,
        explanation: explanation || undefined,
        isValid: errors.length === 0,
        errors,
      };
    });
  };

  // استيراد الأسئلة (مع إزالة التكرار داخل الملف + احترام التكرار في قاعدة البيانات)
  const handleImport = async () => {
    if (categories.length === 0) {
      toast.error('لا توجد فئات نشطة. أضف الفئات من إدارة الفئات أولاً، أو شغّل سكربت قاعدة البيانات CreateDatabase.sql لملء بيانات تجريبية.');
      return;
    }

    const validQuestions = parsedQuestions.filter(q => q.isValid);
    
    if (validQuestions.length === 0) {
      toast.error('لا توجد أسئلة صالحة للاستيراد. تأكد من تطابق أسماء الفئات مع الفئات الموجودة في النظام.');
      return;
    }

    // إزالة التكرار داخل الملف: نفس الفئة + نفس نص السؤال (عربي) = نأخذ الأول فقط
    const seen = new Set<string>();
    const toImport = validQuestions.filter((q) => {
      const key = `${q.categoryId ?? ''}|${(q.questionTextAr ?? '').trim().toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    const duplicateInFileCount = validQuestions.length - toImport.length;

    setImporting(true);
    const createdIds: string[] = [];
    let successCount = 0;
    let duplicateInDbCount = 0;
    let failCount = 0;
    let firstErrorMessage = '';

    for (const question of toImport) {
      try {
        if (!question.categoryId) {
          failCount++;
          if (!firstErrorMessage) firstErrorMessage = `الصف ${question.rowNumber}: الفئة غير محددة أو غير موجودة`;
          continue;
        }
        const result = await questionService.createQuestion({
          categoryId: question.categoryId,
          questionText: question.questionText || question.questionTextAr,
          questionTextAr: question.questionTextAr,
          difficulty: question.difficulty as 'Easy' | 'Medium' | 'Hard',
          options: [
            {
              text: question.answer || question.answerAr,
              textAr: question.answerAr,
              isCorrect: true,
            },
          ],
          timeLimit: question.timeLimit,
          explanation: question.explanation,
          explanationAr: question.explanationAr,
          isActive: isActiveDirectly,
          enableForNewUsers,
          isPromotionQuestion,
        });
        if (result?.questionId) createdIds.push(String(result.questionId));
        successCount++;
      } catch (error: any) {
        const msg = (error?.response?.data?.message ?? error?.response?.data?.error ?? error?.response?.data?.detail ?? error?.message ?? '').toString();
        if (!firstErrorMessage) firstErrorMessage = msg ? `الصف ${question.rowNumber}: ${msg}` : `الصف ${question.rowNumber}: خطأ في الخادم`;
        const isDuplicate = /موجود مسبقاً|duplicate|مكرر/i.test(msg);
        if (isDuplicate) {
          duplicateInDbCount++;
        } else {
          console.error(`Failed to import question ${question.rowNumber}:`, error);
          failCount++;
        }
      }
    }

    setImporting(false);
    setLastImportedIds(createdIds);

    if (failCount === 0) {
      const parts: string[] = [`تم استيراد ${successCount} سؤال بنجاح!`];
      if (duplicateInFileCount > 0) parts.push(`تخطي ${duplicateInFileCount} مكررة في الملف`);
      if (duplicateInDbCount > 0) parts.push(`تخطي ${duplicateInDbCount} موجودة مسبقاً في القاعدة`);
      toast.success(parts.join(' '));
      if (createdIds.length > 0) {
        toast.info('يمكنك حذف كل الأسئلة المستوردة الآن من الأسفل إذا رغبت.');
      }
    } else {
      const parts = [`تم استيراد ${successCount} سؤال`];
      if (duplicateInFileCount > 0) parts.push(`تخطي ${duplicateInFileCount} مكررة في الملف`);
      if (duplicateInDbCount > 0) parts.push(`تخطي ${duplicateInDbCount} موجودة مسبقاً`);
      parts.push(`فشل ${failCount}`);
      toast.warning(parts.join('، '));
      if (firstErrorMessage) toast.error(firstErrorMessage, { duration: 8000 });
    }
  };

  const handleDeleteLastImported = async () => {
    if (lastImportedIds.length === 0) return;
    if (!confirm(`هل أنت متأكد من حذف كل الأسئلة المستوردة (${lastImportedIds.length} سؤال) من قاعدة البيانات؟`)) return;
    try {
      setDeletingImported(true);
      const { deletedCount, notFoundCount } = await questionService.bulkDeleteQuestions(lastImportedIds);
      toast.success(`تم حذف ${deletedCount} سؤال${notFoundCount > 0 ? ` (${notFoundCount} غير موجود)` : ''}`);
      setLastImportedIds([]);
    } catch (error: any) {
      const msg = error?.response?.data?.message ?? error?.response?.data?.error ?? error?.response?.data?.detail ?? error?.message;
      toast.error(msg || 'فشل حذف الأسئلة');
    } finally {
      setDeletingImported(false);
    }
  };

  // حذف سؤال من القائمة
  const removeQuestion = (rowNumber: number) => {
    setParsedQuestions(prev => prev.filter(q => q.rowNumber !== rowNumber));
  };

  // مسح كل الأسئلة
  const clearAll = () => {
    setParsedQuestions([]);
    setRawSheetData([]);
    setFileName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validCount = parsedQuestions.filter(q => q.isValid).length;
  const invalidCount = parsedQuestions.filter(q => !q.isValid).length;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-accent-dark mb-2">استيراد الأسئلة من Excel</h1>
          <p className="text-accent-dark/70">استورد أسئلة متعددة دفعة واحدة من ملف Excel</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/admin/questions')}>
          <ArrowLeft className="w-4 h-4 ml-2" />
          رجوع
        </Button>
      </div>

      {/* تحذير عند عدم وجود فئات (قاعدة بيانات فارغة) */}
      {categories.length === 0 && (
        <Card className="border-2 border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-amber-800">لا توجد فئات نشطة في النظام</p>
                <p className="text-sm text-amber-700 mt-1">
                  استيراد الأسئلة يتطلب وجود فئة واحدة على الأقل. أضف فئات من <strong>إدارة الفئات</strong>، أو شغّل سكربت قاعدة البيانات <strong>CreateDatabase.sql</strong> لملء بيانات تجريبية (أقسام + فئات + أسئلة نموذجية).
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Instructions Card */}
      <Card className="border-2 border-accent-greenLight/20 bg-gradient-to-br from-accent-greenLight/5 to-transparent">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-accent-dark flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-secondary" />
            كيفية الاستخدام
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="flex flex-col items-center text-center p-4 bg-white rounded-xl border border-accent-greenLight/20">
              <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center mb-3">
                <span className="text-2xl font-bold text-secondary">1</span>
              </div>
              <h3 className="font-bold text-accent-dark mb-2">حمّل القالب</h3>
              <p className="text-sm text-accent-dark/60 mb-3">
                حمّل ملف Excel القالب الذي يحتوي على الأعمدة المطلوبة وأمثلة
              </p>
              <Button onClick={downloadTemplate} variant="outline" size="sm" className="border-secondary text-secondary hover:bg-secondary/10">
                <Download className="w-4 h-4 ml-2" />
                تحميل القالب
              </Button>
            </div>

            <div className="flex flex-col items-center text-center p-4 bg-white rounded-xl border border-accent-greenLight/20">
              <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center mb-3">
                <span className="text-2xl font-bold text-secondary">2</span>
              </div>
              <h3 className="font-bold text-accent-dark mb-2">املأ البيانات</h3>
              <p className="text-sm text-accent-dark/60">
                افتح الملف في Excel وأضف الأسئلة. تأكد من وجود الفئة في النظام
              </p>
            </div>

            <div className="flex flex-col items-center text-center p-4 bg-white rounded-xl border border-accent-greenLight/20">
              <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center mb-3">
                <span className="text-2xl font-bold text-secondary">3</span>
              </div>
              <h3 className="font-bold text-accent-dark mb-2">ارفع واستورد</h3>
              <p className="text-sm text-accent-dark/60">
                ارفع الملف، راجع البيانات، ثم اضغط استيراد
              </p>
            </div>
          </div>

          {/* الحقول */}
          <div className="mt-6 p-4 bg-white rounded-xl border border-accent-greenLight/20">
            <h4 className="font-bold text-accent-dark mb-3 flex items-center gap-2">
              <Table className="w-4 h-4" />
              الحقول المطلوبة والاختيارية
            </h4>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-medium text-secondary mb-2">الحقول المطلوبة:</p>
                <ul className="space-y-1 text-accent-dark/70">
                  <li>• <strong>اسم الفئة</strong> - يجب أن تكون فئة موجودة</li>
                  <li>• <strong>نص السؤال (عربي)</strong> - نص السؤال</li>
                  <li>• <strong>الإجابة (عربي)</strong> - الإجابة الصحيحة</li>
                  <li>• <strong>الصعوبة</strong> - سهل / متوسط / صعب</li>
                </ul>
              </div>
              <div>
                <p className="font-medium text-accent-dark/60 mb-2">الحقول الاختيارية:</p>
                <ul className="space-y-1 text-accent-dark/50">
                  <li>• نص السؤال (إنجليزي)</li>
                  <li>• الإجابة (إنجليزي)</li>
                  <li>• الوقت (ثانية) - الافتراضي 30</li>
                  <li>• شرح الإجابة (عربي/إنجليزي)</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upload Area */}
      <Card className="border-2 border-accent-greenLight/20">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-accent-dark flex items-center gap-2">
            <FileUp className="w-5 h-5" />
            رفع ملف Excel
          </CardTitle>
        </CardHeader>
        <CardContent>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileUpload}
            className="hidden"
          />
          
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <LoadingSpinner />
              <p className="mt-4 text-accent-dark/60">جاري قراءة الملف...</p>
            </div>
          ) : parsedQuestions.length === 0 ? (
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-accent-greenLight/40 rounded-xl p-12 text-center cursor-pointer hover:border-secondary hover:bg-secondary/5 transition-all"
            >
              <FileSpreadsheet className="w-16 h-16 mx-auto mb-4 text-accent-dark/30" />
              <p className="text-lg font-medium text-accent-dark mb-2">اسحب ملف Excel هنا أو اضغط للاختيار</p>
              <p className="text-sm text-accent-dark/50">يدعم ملفات .xlsx و .xls</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* File Info & Stats */}
              <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-accent-lightGray/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="w-8 h-8 text-secondary" />
                  <div>
                    <p className="font-medium text-accent-dark">{fileName}</p>
                    <p className="text-sm text-accent-dark/60">{parsedQuestions.length} سؤال</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    <span className="text-green-700">{validCount} صالح</span>
                  </div>
                  {invalidCount > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                      <XCircle className="w-5 h-5 text-red-500" />
                      <span className="text-red-700">{invalidCount} يحتاج مراجعة</span>
                    </div>
                  )}
                  <Button variant="outline" size="sm" onClick={clearAll}>
                    <Trash2 className="w-4 h-4 ml-1" />
                    مسح
                  </Button>
                </div>
              </div>

              {/* Preview Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-accent-greenLight/20">
                      <th className="text-right p-3 font-bold text-accent-dark">الحالة</th>
                      <th className="text-right p-3 font-bold text-accent-dark">الفئة</th>
                      <th className="text-right p-3 font-bold text-accent-dark">السؤال</th>
                      <th className="text-right p-3 font-bold text-accent-dark">الإجابة</th>
                      <th className="text-right p-3 font-bold text-accent-dark">الصعوبة</th>
                      <th className="text-right p-3 font-bold text-accent-dark">الوقت</th>
                      <th className="text-right p-3 font-bold text-accent-dark"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedQuestions.map((question) => (
                      <tr 
                        key={question.rowNumber} 
                        className={`border-b border-accent-greenLight/10 ${
                          !question.isValid ? 'bg-red-50' : 'hover:bg-accent-greenLight/5'
                        }`}
                      >
                        <td className="p-3">
                          {question.isValid ? (
                            <CheckCircle2 className="w-5 h-5 text-green-500" />
                          ) : (
                            <div className="group relative">
                              <AlertCircle className="w-5 h-5 text-red-500 cursor-help" />
                              <div className="absolute z-10 hidden group-hover:block bg-red-100 text-red-800 text-xs p-2 rounded shadow-lg min-w-48 right-0 top-full mt-1">
                                {question.errors.map((err, i) => (
                                  <p key={i}>• {err}</p>
                                ))}
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="p-3 text-accent-dark">{question.categoryName}</td>
                        <td className="p-3 text-accent-dark max-w-xs truncate" title={question.questionTextAr}>
                          {question.questionTextAr}
                        </td>
                        <td className="p-3 text-accent-dark">{question.answerAr}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            question.difficulty === 'Easy' ? 'bg-green-100 text-green-700' :
                            question.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {question.difficulty === 'Easy' ? 'سهل' :
                             question.difficulty === 'Medium' ? 'متوسط' : 'صعب'}
                          </span>
                        </td>
                        <td className="p-3 text-accent-dark">{question.timeLimit}ث</td>
                        <td className="p-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeQuestion(question.rowNumber)}
                            className="text-accent-dark/40 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* خيارات التفعيل (نفس شاشة الأسئلة) */}
      {parsedQuestions.length > 0 && (
        <Card className="border-2 border-accent-greenLight/20">
          <CardHeader>
            <CardTitle className="text-lg font-bold text-accent-dark">خيارات التفعيل للأسئلة المستوردة</CardTitle>
            <p className="text-sm text-accent-dark/60">يمكنك تطبيق نفس خيارات شاشة الأسئلة عند الاستيراد</p>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={enableForNewUsers}
                onChange={(e) => setEnableForNewUsers(e.target.checked)}
                className="w-4 h-4 rounded border-accent-greenLight/40 text-secondary"
              />
              <UserPlus className="w-4 h-4 text-secondary" />
              <span className="text-accent-dark font-medium">تفعيل للمستخدمين الجدد</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isPromotionQuestion}
                onChange={(e) => setIsPromotionQuestion(e.target.checked)}
                className="w-4 h-4 rounded border-accent-greenLight/40 text-secondary"
              />
              <Megaphone className="w-4 h-4 text-secondary" />
              <span className="text-accent-dark font-medium">سؤال ترويج (دعاية)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isActiveDirectly}
                onChange={(e) => setIsActiveDirectly(e.target.checked)}
                className="w-4 h-4 rounded border-accent-greenLight/40 text-secondary"
              />
              <Power className="w-4 h-4 text-secondary" />
              <span className="text-accent-dark font-medium">تفعيل السؤال مباشرة (بدون مراجعة)</span>
            </label>
          </CardContent>
        </Card>
      )}

      {/* Import Button */}
      {parsedQuestions.length > 0 && (
        <div className="flex justify-between items-center flex-wrap gap-4">
          <p className="text-sm text-accent-dark/60">
            سيتم استيراد <strong>{validCount}</strong> سؤال صالح.
            {isActiveDirectly ? ' الأسئلة المستوردة ستكون مفعّلة مباشرة.' : ' الأسئلة المستوردة ستكون معطلة وتحتاج تفعيل.'}
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
              <FileUp className="w-4 h-4 ml-2" />
              رفع ملف آخر
            </Button>
            <Button
              onClick={handleImport}
              disabled={validCount === 0 || importing}
              className="bg-secondary hover:bg-secondary-dark text-white"
            >
              {importing ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span className="mr-2">جاري الاستيراد...</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 ml-2" />
                  استيراد {validCount} سؤال
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* حذف الأسئلة المستوردة في هذه الجلسة */}
      {lastImportedIds.length > 0 && (
        <Card className="border-2 border-accent-red/20 bg-red-50/30">
          <CardHeader>
            <CardTitle className="text-lg font-bold text-accent-dark flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-accent-red" />
              الأسئلة المستوردة في هذه الجلسة
            </CardTitle>
            <p className="text-sm text-accent-dark/60">
              تم استيراد {lastImportedIds.length} سؤال. يمكنك حذفهم كلهم مرة واحدة من قاعدة البيانات.
            </p>
          </CardHeader>
          <CardContent>
            <Button
              variant="outline"
              onClick={handleDeleteLastImported}
              disabled={deletingImported}
              className="border-accent-red/50 text-accent-red hover:bg-accent-red/10"
            >
              {deletingImported ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span className="mr-2">جاري الحذف...</span>
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 ml-2" />
                  حذف كل الأسئلة المستوردة ({lastImportedIds.length}) من قاعدة البيانات
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
